import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../atoms";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { ProUpgradeFunnel } from "@/components/molecules/ProUpgradeFunnel";
import { useProAccess } from "@/hooks/useProAccess";
import { investorFounderAPI } from "@/services/api/investor.service";

interface BusinessPlanPanelProps {
  startupId: string;
}

export const BusinessPlanPanel: React.FC<BusinessPlanPanelProps> = ({ startupId }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const { isPro, isLoading: proLoading } = useProAccess();

  const { data: plan, isLoading } = useQuery({
    queryKey: ["businessPlan", startupId],
    queryFn: () => investorFounderAPI.getBusinessPlan(startupId),
    enabled: Boolean(startupId),
  });

  const generateMutation = useMutation({
    mutationFn: () => investorFounderAPI.generateBusinessPlan(startupId),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["businessPlan", startupId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (proLoading) {
    return (
      <SettingsSectionCard title="Business plan" description="Loading access…">
        <p className="text-sm text-white/55">Checking subscription…</p>
      </SettingsSectionCard>
    );
  }

  if (!isPro) {
    return (
      <div className="space-y-4">
        <ProUpgradeFunnel featureId="business_plan" variant="hero" showFeatureGrid={false} />
        {plan ? (
          <SettingsSectionCard
            title="Existing business plan"
            description="You generated this plan previously. View it anytime; regenerate with Pro."
          >
            <Link
              to="/business-plan"
              className="inline-flex rounded-[14px] border border-white/15 px-4 py-2 text-sm font-semibold text-white/85 hover:bg-white/5"
            >
              View full plan
            </Link>
          </SettingsSectionCard>
        ) : null}
      </div>
    );
  }

  return (
    <SettingsSectionCard
      title="Business plan"
      description="Generate an investor-ready plan from your live burn chart and section drafts. View the full document with charts, then export to PDF."
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="primary"
          size="md"
          disabled={generateMutation.isPending}
          onClick={() => generateMutation.mutate()}
        >
          {generateMutation.isPending
            ? "Generating..."
            : plan
              ? "Regenerate business plan"
              : "Generate business plan"}
        </Button>
        {plan ? (
          <Link
            to="/business-plan"
            className="inline-flex rounded-[14px] border border-white/15 px-4 py-2 text-sm font-semibold text-white/85 hover:bg-white/5"
          >
            View full plan
          </Link>
        ) : null}
        {plan?.generatedAt ? (
          <span className="text-xs text-white/50">
            Last generated {new Date(plan.generatedAt).toLocaleString()}
          </span>
        ) : null}
      </div>

      {error ? <div className="mb-4 text-sm text-danger">{error}</div> : null}

      {generateMutation.isPending ? (
        <div className="space-y-3">
          <p className="text-sm text-white/55">
            Building financial snapshot and generating narrative… this may take 30–60
            seconds.
          </p>
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-16 animate-pulse rounded-[14px] bg-white/5"
            />
          ))}
        </div>
      ) : isLoading ? (
        <div className="text-sm text-white/55">Loading...</div>
      ) : !plan ? (
        <div className="rounded-[14px] border border-dashed border-white/15 p-6 text-sm text-white/55">
          No business plan yet. Add your team roster, business plan context, optional section
          drafts, then generate. Investors will see the plan tab only after generation.
        </div>
      ) : (
        <div className="rounded-[14px] border border-brand/25 bg-brand/10 p-4 text-sm text-white/75">
          Your plan includes live financial charts, KPIs from your startup period, roster-based
          team bios, and AI-written qualitative sections grounded in your context. Open the full
          plan to review or export a PDF.
        </div>
      )}
    </SettingsSectionCard>
  );
};
